import type {
  CreateTourPayload,
  ITour,
  ITourPoi,
  TourCreateRequest,
  TourDetailDTO,
  TourDTO,
  TourUpdateRequest,
  UpdateTourPayload,
} from '@/shared/api/types/tour-types'
import {
  baseTranslationsToLocalizedData,
  localizedDataToBaseTranslations,
} from './localized-data-mapper'
import { mapPoiDtoToPoi } from './poi-mapper'

function mapTourBase(dto: TourDTO): Omit<ITour, 'pois'> {
  return {
    id: dto.id,
    tenantId: dto.tenantId,
    ...(dto.tenantDestinationId !== undefined
      ? { tenantDestinationId: dto.tenantDestinationId }
      : {}),
    localizedData: localizedDataToBaseTranslations(dto.localizedData),
    coordinates: Array.isArray(dto.coordinates) ? dto.coordinates : [],
    duration: dto.duration,
    poiNumber: dto.poiNumber,
    isDeleted: dto.isDeleted,
    isActive: dto.isActive ?? !dto.isDeleted,
    ...(dto.banner ? { banner: dto.banner } : {}),
    ...(dto.thumbnail ? { thumbnail: dto.thumbnail } : {}),
    ...(dto.ticketLink !== null && dto.ticketLink !== undefined
      ? { ticketLink: dto.ticketLink }
      : {}),
    isFavorite: dto.isFavorite ?? false,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

export function mapTourDtoToTour(dto: TourDTO): ITour {
  return {
    ...mapTourBase(dto),
  }
}

export function mapTourDetailDtoToTour(dto: TourDetailDTO): ITour {
  const pois: ITourPoi[] = (dto.pois ?? []).map((poi, index) => ({
    poiId: poi.id,
    order:
      poi.orderIndex !== null && poi.orderIndex !== undefined
        ? poi.orderIndex + 1
        : (poi.order_index ?? index) + 1,
    poi: mapPoiDtoToPoi(poi),
    color: poi.color ?? null,
    favoritePoiRank: poi.favoritePoiRank ?? poi.favorite_poi_rank ?? null,
  }))

  return {
    ...mapTourBase(dto),
    pois,
  }
}

/** Payload nội bộ dùng [lat, lng]; API tour dùng GeoJSON [lng, lat]. */
function mapPayloadCoordinates(payload: CreateTourPayload | UpdateTourPayload): number[][] {
  if (!Array.isArray(payload.coordinates)) {
    return []
  }

  return payload.coordinates.map((pair) => {
    if (!Array.isArray(pair) || pair.length < 2) {
      return pair
    }
    const lat = Number(pair[0])
    const lng = Number(pair[1])
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return pair
    }
    return [lng, lat]
  })
}

export function mapTourCreatePayloadToRequest(payload: CreateTourPayload): TourCreateRequest {
  const request: TourCreateRequest = {
    tenantDestinationId: payload.tenantDestinationId,
    localizedData: baseTranslationsToLocalizedData(payload.localizedData),
    duration: Math.max(30, payload.duration ?? 30),
    coordinates: mapPayloadCoordinates(payload),
    poiIds: payload.poiIds,
  }
  if (payload.banner !== undefined && payload.banner !== '') {
    request.banner = payload.banner
  }
  if (payload.thumbnail !== undefined && payload.thumbnail !== '') {
    request.thumbnail = payload.thumbnail
  }
  if (payload.ticketLink !== undefined) {
    request.ticketLink = payload.ticketLink === '' ? null : payload.ticketLink
  }
  if (payload.isActive !== undefined) {
    request.isActive = payload.isActive
  }
  if (payload.isFavorite !== undefined) {
    request.isFavorite = payload.isFavorite
  }
  return request
}

export function mapTourUpdatePayloadToRequest(payload: UpdateTourPayload): TourUpdateRequest {
  const request: TourUpdateRequest = {}

  if (payload.tenantDestinationId !== undefined) {
    request.tenantDestinationId = payload.tenantDestinationId
  }
  if (payload.localizedData !== undefined) {
    request.localizedData = baseTranslationsToLocalizedData(payload.localizedData)
  }
  if (payload.duration !== undefined) {
    request.duration = Math.max(30, payload.duration)
  }
  if (payload.coordinates !== undefined) {
    request.coordinates = mapPayloadCoordinates(payload)
  }
  if (payload.banner !== undefined) {
    request.banner = payload.banner === '' ? null : payload.banner
  }
  if (payload.thumbnail !== undefined) {
    request.thumbnail = payload.thumbnail === '' ? null : payload.thumbnail
  }
  if ('ticketLink' in payload) {
    request.ticketLink = payload.ticketLink === '' ? null : (payload.ticketLink ?? null)
  }
  if ('isActive' in payload && payload.isActive !== undefined) {
    request.isActive = payload.isActive
  }
  if ('isFavorite' in payload && payload.isFavorite !== undefined) {
    request.isFavorite = payload.isFavorite
  }

  return request
}
